import React, { useEffect, useRef, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  IconButton,
  Link,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import PhoneForwardedOutlinedIcon from "@mui/icons-material/PhoneForwardedOutlined";
import ChecklistRtlOutlinedIcon from "@mui/icons-material/ChecklistRtlOutlined";
import posthog from "../../posthog";
import cinchLogo from "../../assets/cinch-logo.svg";
import { maskEmail, getEmailProviderInbox } from "./signup-email";

const CINCH_BLUE = "#0875BF";
const CINCH_BLUE_DARK = "#055185";
const CINCH_BLUE_LIGHT = "#0994F2";
const CINCH_BLUE_200 = "#BFE4FC";
const CINCH_BLUE_400 = "#44AFF8";
const TEXT_PRIMARY = "#0B0C0E";
const TEXT_SECONDARY = "rgba(1,12,20,0.6)";
const BORDER_LIGHT = "rgba(1,12,20,0.12)";
const APP_BG = "#F4F4F6";
const TRIAL_ORANGE = "#E26008"; // light/orange/500 — trial card border
const WARNING_ORANGE = "#C05107"; // border/warning — FREE TRIAL chip
const HERO_GRADIENT = `linear-gradient(180deg, ${CINCH_BLUE_LIGHT} 25%, ${CINCH_BLUE_DARK})`;

const DEMO_CLIPS = [
  { label: "Service Question", src: "https://marketing.cinch.io/assets/aud/ai-voice-oil-change.mp3" },
  { label: "Coupon Inquiry", src: "https://marketing.cinch.io/assets/aud/ai-voice-coupon.mp3" },
];

const HERO_STEPS = [
  { step: 1, title: "Set your password", time: "1 min" },
  { step: 2, title: "Customize your agent", time: "10 min" },
  { step: 3, title: "Review and approve", time: "4 min" },
];

const STAY_YOURS_CARDS = [
  {
    Icon: SupportAgentOutlinedIcon,
    title: "Anyone who wants a person gets one",
    description: "Give the agent your front desk line and it hands over the call on request.",
  },
  {
    Icon: PhoneForwardedOutlinedIcon,
    title: "Test it out before a customer calls",
    description: "Complete a test call with your agent and then change anything that sounds off.",
  },
  {
    Icon: ChecklistRtlOutlinedIcon,
    title: "Connect your number and go live",
    description: "We’ll give you a new phone number, or you can forward your existing line.",
  },
];

const FAQS = [
  {
    q: "Will the agent sound robotic?",
    a: "No. The AI Voice agent is designed to sound natural, professional, and conversational—so callers feel like they’re speaking with a real assistant.",
  },
  {
    q: "What if the agent can’t answer the question?",
    a: "If the voice agent doesn’t have access to the information required, it will offer to take a message or transfer the call to a team member who can assist from there.",
  },
  {
    q: "Does this replace my team?",
    a: "No. It supports your team by handling calls when they’re busy or unavailable—so no caller gets ignored.",
  },
  {
    q: "Is getting set up complicated?",
    a: "Not at all. Most businesses are live in under 10 minutes.",
  },
  {
    q: "Does this actually help my business make more money?",
    a: "Absolutely. It makes sure every call is answered and every lead is captured. Businesses use AI Voice to protect revenue they were already losing to missed calls.",
  },
];

const WAVEFORM_BAR_COUNT = 34;

const STATIC_BAR_HEIGHTS = [
  17, 13, 21, 35, 26, 29, 37, 31, 40, 38, 28, 19, 16, 18, 34, 8, 12, 10, 16, 12, 16, 12, 8, 7, 15, 15, 22, 20, 12, 22, 18, 30, 12, 26,
];

const formatClock = (seconds) => {
  if (!Number.isFinite(seconds) || seconds < 0) return "00:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

// maxWidth 1312 + 32px padding → ~1248px content, matching the 1440 design's
// 96px side gutters. px stays 16 on mobile to match the design's 16px gutter.
const CONTAINER_SX = { maxWidth: 1312, mx: "auto", px: { xs: 2, sm: 4 }, width: "100%", boxSizing: "border-box" };

const RecordingCard = ({ clip }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [barHeights, setBarHeights] = useState(STATIC_BAR_HEIGHTS);
  const audioRef = useRef(null);

  useEffect(() => {
    if (!isPlaying) return undefined;
    const interval = setInterval(() => {
      setBarHeights(
        STATIC_BAR_HEIGHTS.map((base) => {
          const wiggle = (Math.random() - 0.5) * base * 0.7;
          return Math.max(4, Math.min(40, base + wiggle));
        }),
      );
    }, 80);
    return () => clearInterval(interval);
  }, [isPlaying]);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) {
      audio.play().catch(() => setIsPlaying(false));
    } else {
      audio.pause();
    }
  };

  const progressFraction = duration > 0 ? Math.min(1, currentTime / duration) : 0;
  const filledBars = Math.round(progressFraction * WAVEFORM_BAR_COUNT);

  return (
    <Paper elevation={0} sx={{ flex: 1, minWidth: 0, bgcolor: "#fff", borderRadius: "16px", p: 2, border: `1px solid ${CINCH_BLUE_LIGHT}`, boxShadow: "2px 2px 12px 0px rgba(0,0,0,0.12)" }}>
      <audio
        ref={audioRef}
        src={clip.src}
        preload="auto"
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onTimeUpdate={(e) => setCurrentTime(e.currentTarget.currentTime)}
        onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
        onEnded={() => {
          setIsPlaying(false);
          setCurrentTime(0);
          setBarHeights(STATIC_BAR_HEIGHTS);
        }}
      />
      <Stack spacing={2}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={1} alignItems="center">
            <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: "#0EB450" }} />
            <Typography sx={{ fontWeight: 500, fontSize: "12px", color: TEXT_PRIMARY, lineHeight: "20px", letterSpacing: "0.1px" }}>{`Live call — ${clip.label}`}</Typography>
          </Stack>
          <Typography sx={{ fontWeight: 500, fontSize: "12px", color: TEXT_PRIMARY, lineHeight: "20px", letterSpacing: "0.1px" }}>
            {formatClock(currentTime || (duration && !isPlaying ? duration : 0))}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <IconButton
            onClick={togglePlay}
            sx={{ "width": 60, "height": 60, "bgcolor": CINCH_BLUE, "color": "#fff", "flexShrink": 0, "&:hover": { bgcolor: CINCH_BLUE_DARK } }}
            aria-label={isPlaying ? `Pause ${clip.label} demo call` : `Play ${clip.label} demo call`}
          >
            {isPlaying ? <PauseIcon sx={{ fontSize: 32 }} /> : <PlayArrowIcon sx={{ fontSize: 32, ml: "2px" }} />}
          </IconButton>
          <Box sx={{ flex: 1, height: 40, display: "flex", alignItems: "center", gap: "4px" }}>
            {barHeights.map((h, i) => (
              <Box key={i} sx={{ flex: 1, height: `${h}px`, bgcolor: i < filledBars ? CINCH_BLUE : "#D5D7DD", borderRadius: "1px", transition: "height 80ms linear" }} />
            ))}
          </Box>
        </Stack>
      </Stack>
    </Paper>
  );
};

const Band = ({ bg, children }) => (
  <Box sx={{ bgcolor: bg }}>
    <Box sx={{ ...CONTAINER_SX, py: { xs: 4, sm: 5 } }}>{children}</Box>
  </Box>
);

const SectionHeading = ({ title, subtitle }) => (
  <Stack spacing={0.25} sx={{ mb: 3 }}>
    <Typography sx={{ fontWeight: 700, fontSize: "20px", color: TEXT_PRIMARY, lineHeight: "28px" }}>{title}</Typography>
    {subtitle ? <Typography sx={{ fontWeight: 400, fontSize: "16px", color: TEXT_SECONDARY, lineHeight: "28px", letterSpacing: "0.15px" }}>{subtitle}</Typography> : null}
  </Stack>
);

const StayYoursCard = ({ Icon, title, description }) => (
  <Paper elevation={0} sx={{ flex: 1, minWidth: 0, p: 3, borderRadius: "16px", bgcolor: "rgba(255,255,255,0.5)", border: `1px solid ${BORDER_LIGHT}` }}>
    <Stack spacing={2}>
      <Box sx={{ width: 32, height: 32, borderRadius: "8px", bgcolor: CINCH_BLUE_200, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Icon sx={{ fontSize: 20, color: CINCH_BLUE_DARK }} />
      </Box>
      <Typography sx={{ fontWeight: 700, fontSize: "16px", color: TEXT_PRIMARY, lineHeight: "24px", letterSpacing: "0.25px" }}>{title}</Typography>
      <Typography sx={{ fontWeight: 500, fontSize: "16px", color: TEXT_SECONDARY, lineHeight: "24px", letterSpacing: "0.15px" }}>{description}</Typography>
    </Stack>
  </Paper>
);

/**
 * AI Voice Agent signup confirmation page (CIN-6934). Shown to every prospect
 * who submits the AI Voice PLG form — identical regardless of whether the email
 * is new or already has an account, so the page can't enumerate Cinch users.
 * The masked address is the user's own submitted value. The blue header is
 * sticky and collapses to a compact bar on scroll.
 */
export default function VoiceAgentSuccess({ email, onStartOver, featureFocus }) {
  const maskedEmail = maskEmail(email);
  const inboxUrl = getEmailProviderInbox(email);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  const heroRef = useRef(null);

  useEffect(() => {
    document.title = "Let’s Activate Your Account — Cinch";
    posthog.capture("signup_success_viewed", { feature_focus: featureFocus });
  }, [featureFocus]);

  // Show the compact bar once the hero has (almost) scrolled off. The hero never
  // resizes — it just scrolls away and a slim fixed bar slides in over it — so
  // there's no large reflow, the motion is smooth, and a plain scroll threshold
  // is stable (no anchoring/oscillation to fight).
  useEffect(() => {
    const onScroll = () => {
      const heroHeight = heroRef.current ? heroRef.current.offsetHeight : 300;
      setCollapsed(window.scrollY > heroHeight - 72);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  const emailChip = maskedEmail ? (
    <Box sx={{ bgcolor: "rgba(255,255,255,0.1)", border: `1px solid ${CINCH_BLUE_400}`, borderRadius: "8px", px: 1.5, py: 0.5, display: "inline-flex" }}>
      <Typography sx={{ fontWeight: 600, fontSize: "16px", color: "#fff", lineHeight: "24px", letterSpacing: "0.15px" }}>{maskedEmail}</Typography>
    </Box>
  ) : null;

  const sendItAgain = onStartOver ? (
    <>
      , or{" "}
      <Link
        component="button"
        type="button"
        onClick={onStartOver}
        underline="always"
        sx={{ color: "#fff", textDecorationColor: "#fff", font: "inherit", fontWeight: 400, verticalAlign: "baseline", p: 0, border: 0, background: "none", cursor: "pointer" }}
      >
        send it again
      </Link>
    </>
  ) : null;

  const openMyEmailButton = inboxUrl ? (
    <Button
      variant="contained"
      href={inboxUrl}
      target="_blank"
      rel="noopener noreferrer"
      startIcon={<MailOutlineIcon sx={{ fontSize: "20px !important" }} />}
      sx={{
        "bgcolor": "#fff",
        "color": CINCH_BLUE,
        "fontWeight": 600,
        "fontSize": "16px",
        "textTransform": "none",
        "borderRadius": "8px",
        "px": 4,
        "py": 1,
        "minHeight": 40,
        "flexShrink": 0,
        "boxShadow": "0px 1px 5px 0px rgba(0,0,0,0.12), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 3px 1px -2px rgba(0,0,0,0.2)",
        "&:hover": { bgcolor: "rgba(255,255,255,0.92)" },
      }}
    >
      Open My Email
    </Button>
  ) : null;

  const heroSteps = (
    <Stack spacing={1}>
      {HERO_STEPS.map(({ step, title, time }) => (
        <Stack key={step} direction="row" spacing={1.5} alignItems="center">
          <Box sx={{ width: 24, height: 24, borderRadius: "50%", bgcolor: CINCH_BLUE_200, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <Typography sx={{ fontWeight: 700, fontSize: "12px", color: TEXT_PRIMARY, lineHeight: "20px" }}>{step}</Typography>
          </Box>
          <Typography sx={{ fontWeight: 600, fontSize: "16px", color: "#fff", lineHeight: "24px", letterSpacing: "0.15px", flex: 1 }}>{title}</Typography>
          <Typography sx={{ fontWeight: 400, fontSize: "16px", color: "#fff", lineHeight: "24px" }}>{time}</Typography>
        </Stack>
      ))}
    </Stack>
  );

  return (
    <Box sx={{ position: "relative", minHeight: "100vh", bgcolor: "#fff", boxSizing: "border-box" }}>
        {/* Compact bar — fixed, slides down over the page once the hero has
            scrolled off. Nothing resizes, so the motion stays smooth. Mobile
            shows just the title + button; sm+ adds the email line. */}
        <Box
          aria-hidden={!collapsed}
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1200,
            background: HERO_GRADIENT,
            transform: collapsed ? "translateY(0)" : "translateY(-100%)",
            transition: "transform 0.28s ease",
            boxShadow: collapsed ? "0 2px 10px rgba(0,0,0,0.18)" : "none",
            willChange: "transform",
          }}
        >
          <Box sx={{ ...CONTAINER_SX, py: { xs: 1.5, sm: 2 } }}>
            <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="center">
              <Stack spacing={0.5} sx={{ minWidth: 0 }}>
                <Stack direction="row" spacing={1.5} alignItems="baseline" flexWrap="wrap" useFlexGap>
                  <Typography sx={{ fontWeight: 700, fontSize: { xs: "18px", sm: "24px" }, color: "#fff", lineHeight: "32px" }}>Let’s Activate Your Account</Typography>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ display: { xs: "none", sm: "flex" } }}>
                    <Typography sx={{ fontWeight: 400, fontSize: "14px", color: "rgba(255,255,255,0.85)", lineHeight: "22px" }}>We sent a setup link to</Typography>
                    {emailChip}
                  </Stack>
                </Stack>
                <Typography sx={{ fontWeight: 400, fontSize: "14px", color: "rgba(255,255,255,0.85)", lineHeight: "22px", display: { xs: "none", sm: "block" } }}>
                  Not there? Check spam{sendItAgain}
                </Typography>
              </Stack>
              {openMyEmailButton}
            </Stack>
          </Box>
        </Box>

        {/* Full hero — full-bleed blue; scrolls away naturally. Two columns on
            md, full-height with the timeline pinned to the bottom on mobile. */}
        <Box ref={heroRef} sx={{ background: HERO_GRADIENT }}>
          <Box sx={{ ...CONTAINER_SX, py: { xs: 4, sm: 5 } }}>
            <Stack spacing={{ xs: 4, md: 3 }}>
              <Stack spacing={4}>
                <Box
                  sx={{
                    width: 96,
                    height: 32,
                    bgcolor: "#fff",
                    mask: `url(${cinchLogo}) no-repeat left center / contain`,
                    WebkitMask: `url(${cinchLogo}) no-repeat left center / contain`,
                  }}
                />
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Box sx={{ border: "1px solid #fff", borderRadius: "100px", px: 1, py: 0.5, display: "flex", alignItems: "center", minHeight: 24 }}>
                    <Typography sx={{ fontWeight: 500, fontSize: "12px", color: "#fff", lineHeight: "16px", letterSpacing: "0.16px" }}>NEW FEATURE</Typography>
                  </Box>
                  <Typography sx={{ fontWeight: 700, fontSize: "16px", color: "#fff", lineHeight: "24px", letterSpacing: "0.25px" }}>AI Voice Agent</Typography>
                </Stack>
              </Stack>

              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={{ xs: 4, md: 5 }}
                justifyContent="space-between"
                alignItems={{ xs: "stretch", md: "flex-start" }}
              >
                <Stack spacing={2} sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: "28px", color: "#fff", lineHeight: "36px" }}>Let’s Activate Your Account</Typography>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Typography sx={{ fontWeight: 600, fontSize: "16px", color: "#fff", lineHeight: "24px", letterSpacing: "0.15px" }}>We sent a setup link to</Typography>
                    {emailChip}
                    <Typography sx={{ fontWeight: 600, fontSize: "16px", color: "#fff", lineHeight: "24px", letterSpacing: "0.15px" }}>Open it to set your password and get started.</Typography>
                  </Stack>
                  {openMyEmailButton ? <Box>{openMyEmailButton}</Box> : null}
                  <Typography sx={{ fontWeight: 400, fontSize: "16px", color: "rgba(255,255,255,0.85)", lineHeight: "24px" }}>
                    Not there? Check spam{sendItAgain}
                  </Typography>
                </Stack>

                <Stack spacing={2} sx={{ flexShrink: 0 }}>
                  <Stack spacing={0.25}>
                    <Typography sx={{ fontWeight: 600, fontSize: "16px", color: "#fff", lineHeight: "24px", letterSpacing: "0.15px" }}>You Are 15 Minutes From a Live Agent</Typography>
                    <Typography sx={{ fontWeight: 400, fontSize: "16px", color: "rgba(255,255,255,0.85)", lineHeight: "24px" }}>Get started with 3 quick steps.</Typography>
                  </Stack>
                  {heroSteps}
                </Stack>
              </Stack>
            </Stack>
          </Box>
        </Box>

        {/* Listen to a real call — white band */}
        <Band bg="#fff">
          <SectionHeading title="Listen to a Real Call Before You Get Started" subtitle="Click play and hear how one agent helps callers for a local business." />
          <Stack direction={{ xs: "column", sm: "row" }} spacing={3} sx={{ maxWidth: 820 }}>
            {DEMO_CLIPS.map((clip) => (
              <RecordingCard key={clip.src} clip={clip} />
            ))}
          </Stack>
        </Band>

        {/* Nothing goes live — grey band */}
        <Band bg={APP_BG}>
          <SectionHeading title="Nothing Goes Live Until You Say So" subtitle="Review and test your agent before it goes live for your customers." />
          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            {STAY_YOURS_CARDS.map((card) => (
              <StayYoursCard key={card.title} {...card} />
            ))}
          </Stack>
        </Band>

        {/* Cinch AI Voice is just the beginning — white band */}
        <Band bg="#fff">
          <SectionHeading title="Cinch AI Voice Is Just the Beginning" />
          <Stack spacing={3}>
            <Typography sx={{ fontWeight: 400, fontSize: "16px", color: TEXT_PRIMARY, lineHeight: "28px", letterSpacing: "0.15px" }}>
              Cinch is the lifecycle marketing platform that local businesses use to bring customers back. AI Voice is one part of it, turning every
              conversation into data then activating it across text, email, direct mail, and more. One call becomes a lifelong customer relationship.
            </Typography>
            <Paper elevation={0} sx={{ borderRadius: "16px", border: `1px solid ${TRIAL_ORANGE}`, p: 3, boxShadow: "0px 1px 10px 0px rgba(0,0,0,0.12), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 2px 4px 0px rgba(0,0,0,0.2)" }}>
              <Stack spacing={1.5}>
                <Stack direction={{ xs: "column-reverse", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} spacing={1.5}>
                  <Typography sx={{ fontWeight: 700, fontSize: "16px", color: TEXT_PRIMARY, lineHeight: "24px", letterSpacing: "0.25px" }}>Your AI Voice Agent Trial</Typography>
                  <Box sx={{ border: `1px solid ${WARNING_ORANGE}`, borderRadius: "100px", px: 1.5, py: 0.5, flexShrink: 0 }}>
                    <Typography sx={{ fontWeight: 500, fontSize: "12px", color: WARNING_ORANGE, lineHeight: "16px", letterSpacing: "0.16px", whiteSpace: "nowrap" }}>FREE TRIAL</Typography>
                  </Box>
                </Stack>
                <Typography sx={{ fontWeight: 500, fontSize: "16px", color: TEXT_SECONDARY, lineHeight: "24px", letterSpacing: "0.15px" }}>
                  Try it free for 25 minutes on your first AI agent. When time&rsquo;s up, the agent pauses. Ready to keep going? Plans start at $99/month.
                </Typography>
              </Stack>
            </Paper>
          </Stack>
        </Band>

        {/* FAQ — grey band, white card */}
        <Band bg={APP_BG}>
          <Paper elevation={0} sx={{ bgcolor: "#fff", borderRadius: "16px", border: `1px solid ${BORDER_LIGHT}` }}>
            <Box sx={{ px: 3, pt: 3, pb: 2, borderBottom: `1px solid ${BORDER_LIGHT}` }}>
              <Typography sx={{ fontWeight: 700, fontSize: "20px", color: TEXT_PRIMARY, lineHeight: "28px" }}>Frequently Asked Questions</Typography>
              <Typography sx={{ fontWeight: 400, fontSize: "16px", color: TEXT_SECONDARY, lineHeight: "28px", letterSpacing: "0.15px" }}>Get answers to common questions about our AI Voice Agent.</Typography>
            </Box>
            <Box sx={{ p: 3 }}>
              {FAQS.map(({ q, a }, i) => (
                <Accordion
                  key={q}
                  elevation={0}
                  disableGutters
                  expanded={expandedFaq === i}
                  onChange={() => setExpandedFaq(expandedFaq === i ? null : i)}
                  sx={{ "bgcolor": "#fff", "borderBottom": `1px solid ${BORDER_LIGHT}`, "&:before": { display: "none" }, "&.Mui-expanded": { mb: 0 } }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: TEXT_PRIMARY }} />}
                    sx={{ "px": 2, "py": 1.5, "minHeight": "auto", "& .MuiAccordionSummary-content": { my: 0 } }}
                  >
                    <Typography sx={{ fontWeight: 500, fontSize: "16px", color: TEXT_PRIMARY, lineHeight: "24px", letterSpacing: "0.15px" }}>{q}</Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 2, pt: 0, pb: 2 }}>
                    <Typography sx={{ fontWeight: 400, fontSize: "14px", color: TEXT_PRIMARY, lineHeight: "22px" }}>{a}</Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          </Paper>
        </Band>
      </Box>
  );
}
